<template>
  <div class="fixed z-[70] inset-0 overflow-y-auto">
    <div class="flex items-center py-8 justify-center min-h-screen">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-black opacity-50"></div>
      </div>
      <div class="bg-white rounded-lg overflow-hidden shadow-xl px-6 py-6 z-50 max-w-[94vw] min-w-[94vw] md:min-w-[82vw] lg:min-w-[76vw] md:max-w-[82vw] lg:max-w-[76vw]">
        <div class="flex justify-between items-center mb-6">
          <div>
            <h2 class="text-xl font-medium text-gray-800">
              {{ modalTitle }}
            </h2>
            <p class="text-sm text-gray-500 mt-1">
              {{ modalDescription }}
            </p>
          </div>
          <button
            @click="closeModal"
            class="text-gray-500 hover:text-gray-700 w-8 h-8 flex items-center justify-center hover:bg-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="saving"
          >
            <svg class="w-5 h-5">
              <use href="../../../../assets/svg/icon.svg#cancel" />
            </svg>
          </button>
        </div>

        <div v-if="allowProductSelection && !hasSelectedProduct" class="mb-5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
          <div class="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px] items-start">
            <div class="relative">
              <label class="block">
                <span class="block mb-2 text-sm font-medium text-gray-800">Buscar producto</span>
                <div class="flex items-center relative text-gray-600">
                  <div class="absolute top-0 left-3 w-6 h-full flex items-center">
                    <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.35-4.35" />
                    </svg>
                  </div>
                  <input
                    v-model="productSearchTerm"
                    type="search"
                    class="border-2 pl-9 py-2 text-sm outline-none w-full rounded-md bg-gray-50 focus:border-blue-500"
                    placeholder="Buscar producto por nombre o codigo de barra"
                    @input="handleProductSearchInput"
                    @keydown.enter.prevent="searchSelectableProducts"
                    autocomplete="off"
                  />
                </div>
                <span class="block mt-2 text-xs text-slate-500">
                  Busca el producto para crearle una oferta desde este submódulo.
                </span>
              </label>

              <div
                v-if="showProductSearchResults"
                class="absolute top-[4.5rem] left-0 right-0 z-20 py-2 bg-white border border-gray-300 rounded-md shadow-lg max-h-72 overflow-y-auto"
              >
                <div v-if="productSearchLoading" class="px-3">
                  <div class="px-3 py-3 bg-gray-200 animate-pulse rounded-lg mb-2"></div>
                  <div class="px-3 py-3 bg-gray-200 animate-pulse rounded-lg mb-2"></div>
                </div>

                <div v-else-if="productSearchResults.length <= 0" class="px-3 py-1 bg-gray-50 text-gray-400">
                  <strong>{{ productSearchTerm }}</strong> no existe en nuestra base de datos
                </div>

                <div
                  v-for="result in productSearchResults"
                  :key="result.id"
                  class="odd:bg-white even:bg-slate-50 text-gray-600"
                >
                  <div
                    class="py-2 px-3 pb-1 cursor-pointer hover:bg-gray-100 hover:text-blue-500 uppercase"
                    @click="selectProductForOffer(result)"
                  >
                    {{ result.nombre_producto }}
                    <div class="flex flex-wrap gap-2 mt-1">
                      <span class="px-3 py-1 rounded-md text-slate-500 bg-slate-50 text-xs font-semibold normal-case">
                        {{ result.codigo_de_barra || "Sin codigo" }}
                      </span>
                      <span class="px-3 py-1 rounded-md text-orange-500 bg-orange-50 text-xs font-semibold normal-case">
                        En stock: {{ result.stock }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-3 min-h-[104px]">
              <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Producto seleccionado</p>
              <template v-if="hasSelectedProduct">
                <p class="mt-2 text-sm font-semibold text-slate-700">{{ productName }}</p>
                <p class="text-xs text-slate-500 mt-1">{{ productBarCode || "Sin codigo de barra" }}</p>
              </template>
              <p v-else class="mt-3 text-sm text-slate-500">
                Selecciona un producto para habilitar la configuracion de la oferta.
              </p>
            </div>
          </div>

        </div>

        <div v-else-if="allowProductSelection && hasSelectedProduct" class="mb-5 rounded-xl border border-green-100 bg-green-50 px-4 py-4">
          <p class="text-xs font-semibold uppercase tracking-wide text-green-700">Producto seleccionado</p>
          <div class="mt-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p class="text-sm font-semibold text-slate-700">{{ productName }}</p>
              <p class="text-xs text-slate-500">{{ productBarCode || "Sin codigo de barra" }}</p>
            </div>
            <span class="inline-flex px-3 py-1 rounded-md text-xs font-semibold text-green-700 bg-white border border-green-200 w-fit">
              Oferta en edición
            </span>
          </div>
        </div>

        <div class="flex flex-wrap gap-3 mb-4">
          <button
            class="px-3 py-2 bg-blue-500 text-white rounded-md text-sm font-semibold hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed"
            @click="addPromotion"
            :disabled="saving || (allowProductSelection && !hasSelectedProduct)"
          >
            Agregar promocion
          </button>
          <div class="px-3 py-2 bg-slate-100 text-slate-600 rounded-md text-sm font-semibold">
            {{ allowProductSelection && !hasSelectedProduct ? "Selecciona un producto para crear la oferta" : promotionSummary.text }}
          </div>
        </div>

        <div v-if="allowProductSelection && !hasSelectedProduct" class="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center text-sm text-slate-500">
          Usa el buscador para elegir el producto y luego configurar su oferta.
        </div>

        <div v-else class="space-y-4 max-h-[65vh] overflow-y-auto pr-2">
          <div
            v-for="(promotion, index) in localPromotions"
            :key="index"
            class="border border-slate-200 rounded-xl p-4 bg-slate-50"
          >
            <div class="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <h3 class="text-sm font-semibold text-slate-700 uppercase">
                  {{ promotion.name || `Promocion ${index + 1}` }}
                </h3>
                <p class="text-xs text-slate-500 mt-1">
                  {{ getPromotionTypeLabel(promotion.type) }}
                </p>
              </div>
              <button
                class="px-3 py-1.5 text-xs font-semibold rounded-md bg-red-100 text-red-600 hover:bg-red-500 hover:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                @click="removePromotion(index)"
                :disabled="saving"
              >
                Quitar
              </button>
            </div>

            <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <label class="block">
                <span class="block mb-2 text-sm font-medium text-gray-800">Nombre</span>
                <input
                  v-model="promotion.name"
                  type="text"
                  class="py-2 px-2 text-sm bg-white outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500"
                  maxlength="120"
                />
              </label>
              <label class="block">
                <span class="block mb-2 text-sm font-medium text-gray-800">Tipo de oferta</span>
                <select
                  v-model="promotion.type"
                  class="py-2 px-3 text-sm text-gray-600 border-2 focus:border-blue-500 block w-full rounded-lg bg-white outline-none cursor-pointer"
                  @change="handleTypeChange(promotion)"
                >
                  <option :value="PROMOTION_TYPES.SPECIAL_PRICE">Precio especial</option>
                  <option :value="PROMOTION_TYPES.QUANTITY_BONUS">Cantidad bonificada</option>
                  <option :value="PROMOTION_TYPES.BUNDLE">Lote, combo o paquete</option>
                </select>
              </label>
              <label class="block">
                <span class="block mb-2 text-sm font-medium text-gray-800">Estado</span>
                <select
                  v-model="promotion.status"
                  class="py-2 px-3 text-sm text-gray-600 border-2 focus:border-blue-500 block w-full rounded-lg bg-white outline-none cursor-pointer"
                >
                  <option value="ACTIVA">Activa</option>
                  <option value="INACTIVA">Inactiva</option>
                  <option value="BORRADOR">Borrador</option>
                </select>
              </label>
              <label class="block">
                <span class="block mb-2 text-sm font-medium text-gray-800">Aplicar a</span>
                <select
                  class="py-2 px-3 text-sm text-gray-600 border-2 focus:border-blue-500 block w-full rounded-lg bg-white outline-none cursor-pointer"
                  :value="getPromotionTargetKey(promotion)"
                  :disabled="unitOptionsLoading || unitOptions.length <= 0"
                  @change="changePromotionTarget(promotion, $event.target.value)"
                >
                  <option v-if="unitOptions.length <= 0" value="" disabled>
                    Sin unidades registradas
                  </option>
                  <option v-for="unitOption in unitOptions" :key="unitOption.key" :value="unitOption.key">
                    {{ unitOption.label }}
                  </option>
                </select>
                <span class="block mt-2 text-xs text-slate-500">
                  {{ unitOptionsLoading ? "Cargando unidades de medida del producto..." : "La oferta se evaluara solamente cuando la venta use esa unidad de medida." }}
                </span>
              </label>
              <label class="block">
                <span class="block mb-2 text-sm font-medium text-gray-800">Inicio</span>
                <input
                  v-model="promotion.startAt"
                  type="datetime-local"
                  class="py-2 px-2 text-sm bg-white outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500"
                />
              </label>
              <label class="block">
                <span class="block mb-2 text-sm font-medium text-gray-800">Fin</span>
                <input
                  v-model="promotion.endAt"
                  type="datetime-local"
                  :min="promotion.startAt || undefined"
                  class="py-2 px-2 text-sm bg-white outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500"
                />
                <span class="block mt-2 text-xs text-slate-500">
                  Al llegar esta fecha, el producto vuelve automáticamente a su precio original.
                </span>
              </label>
              <label class="block md:col-span-2 lg:col-span-3">
                <span class="block mb-2 text-sm font-medium text-gray-800">Descripcion</span>
                <textarea
                  v-model="promotion.description"
                  rows="2"
                  maxlength="300"
                  class="py-2 px-2 text-sm bg-white outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500 resize-none"
                ></textarea>
              </label>
            </div>

            <div class="mt-4 rounded-lg border border-slate-200 bg-white px-4 py-3">
              <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Comportamiento
              </p>
              <p
                v-if="promotion.type === PROMOTION_TYPES.SPECIAL_PRICE"
                class="mt-2 text-sm text-slate-600"
              >
                Se aplica automaticamente al producto cuando cumple la cantidad minima configurada.
              </p>
              <p
                v-else-if="promotion.type === PROMOTION_TYPES.QUANTITY_BONUS"
                class="mt-2 text-sm text-slate-600"
              >
                El sistema calcula la bonificacion automaticamente sobre la venta configurada sin pedir parametros internos adicionales.
              </p>
              <p v-else class="mt-2 text-sm text-slate-600">
                El paquete se calcula automaticamente con los productos y cantidades configuradas.
              </p>
              <p class="mt-2 text-xs font-semibold text-blue-600">
                Unidad de medida: {{ promotion.targetUnitLabel || "Sin unidad seleccionada" }}
              </p>
            </div>

            <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4" v-if="promotion.type === PROMOTION_TYPES.SPECIAL_PRICE">
              <label class="block">
                <span class="block mb-2 text-sm font-medium text-gray-800">Precio actual</span>
                <input
                  :value="getCurrentPriceLabel(promotion)"
                  type="text"
                  disabled
                  class="py-2 px-2 text-sm bg-slate-100 outline-none rounded-lg text-gray-600 w-full border-2 border-slate-200"
                />
              </label>
              <label class="block">
                <span class="block mb-2 text-sm font-medium text-gray-800">Cantidad minima</span>
                <input
                  v-model="promotion.requiredQuantity"
                  type="number"
                  min="1"
                  step="1"
                  class="py-2 px-2 text-sm bg-white outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500"
                />
              </label>
              <label class="block">
                <span class="block mb-2 text-sm font-medium text-gray-800">Precio de oferta</span>
                <input
                  v-model="promotion.specialPrice"
                  type="number"
                  min="0"
                  step="0.01"
                  class="py-2 px-2 text-sm bg-white outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500"
                />
              </label>
            </div>

            <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4" v-if="promotion.type === PROMOTION_TYPES.QUANTITY_BONUS">
              <label class="block">
                <span class="block mb-2 text-sm font-medium text-gray-800">Venta minima</span>
                <input
                  v-model="promotion.requiredQuantity"
                  type="number"
                  min="1"
                  step="1"
                  class="py-2 px-2 text-sm bg-white outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500"
                  placeholder="Ej.: 20"
                />
                <span class="block mt-2 text-xs text-slate-500">
                  Cantidad que el cliente debe llevar en la venta para activar la bonificacion. Ejemplo: 20.
                </span>
              </label>
              <label class="block">
                <span class="block mb-2 text-sm font-medium text-gray-800">Lleva gratis</span>
                <input
                  v-model="promotion.bonusQuantity"
                  type="number"
                  min="1"
                  step="1"
                  class="py-2 px-2 text-sm bg-white outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500"
                  placeholder="Ej.: 1"
                />
                <span class="block mt-2 text-xs text-slate-500">
                  Unidades bonificadas que se agregan automaticamente cuando se cumple la venta minima.
                </span>
              </label>
            </div>

            <div v-if="promotion.type === PROMOTION_TYPES.BUNDLE" class="mt-4 space-y-4">
              <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <label class="block">
                  <span class="block mb-2 text-sm font-medium text-gray-800">Precio del paquete</span>
                  <input
                    v-model="promotion.bundlePrice"
                    type="number"
                    min="0"
                    step="0.01"
                    class="py-2 px-2 text-sm bg-white outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500"
                  />
                </label>
                <label class="block md:col-span-2">
                  <span class="block mb-2 text-sm font-medium text-gray-800">Buscar productos relacionados</span>
                  <div class="flex gap-2">
                    <input
                      v-model="searchTerms[index]"
                      type="text"
                      class="py-2 px-2 text-sm bg-white outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500"
                      placeholder="Nombre, codigo o descripcion"
                      @keydown.enter.prevent="searchProducts(index)"
                    />
                    <button
                      class="px-3 py-2 rounded-md bg-slate-800 text-white text-sm font-semibold hover:bg-slate-900"
                      @click="searchProducts(index)"
                    >
                      Buscar
                    </button>
                  </div>
                </label>
              </div>

              <div v-if="searchLoadingIndex === index" class="text-sm text-slate-500">
                Buscando productos...
              </div>

              <div v-if="searchResults[index]?.length" class="border border-slate-200 rounded-lg bg-white p-3">
                <p class="text-xs font-semibold text-slate-500 uppercase mb-2">Resultados</p>
                <div class="space-y-2 max-h-40 overflow-y-auto">
                  <button
                    v-for="result in searchResults[index]"
                    :key="result.id"
                    class="w-full text-left px-3 py-2 rounded-md border border-slate-200 hover:border-blue-400 hover:bg-blue-50"
                    @click="addCompanionProduct(index, result)"
                  >
                    <span class="block text-sm font-semibold text-slate-700">{{ result.nombre_producto }}</span>
                    <span class="block text-xs text-slate-500">{{ result.codigo_de_barra }}</span>
                  </button>
                </div>
              </div>

              <div class="border border-slate-200 rounded-lg bg-white p-3">
                <p class="text-xs font-semibold text-slate-500 uppercase mb-2">Productos del paquete</p>
                <div class="space-y-2">
                  <div
                    v-for="(item, itemIndex) in promotion.items"
                    :key="`${index}-${itemIndex}`"
                    class="grid md:grid-cols-[1fr_120px_120px] gap-3 items-center"
                  >
                    <div class="text-sm text-slate-700 font-medium">
                      {{ item.name || productName || 'Producto principal' }}
                    </div>
                    <input
                      v-model="item.quantity"
                      type="number"
                      min="1"
                      step="1"
                      class="py-2 px-2 text-sm bg-slate-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500"
                    />
                    <button
                      class="px-3 py-2 rounded-md text-sm font-semibold"
                      :class="itemIndex === 0 ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-red-100 text-red-600 hover:bg-red-500 hover:text-white'"
                      @click="removePromotionItem(index, itemIndex)"
                      :disabled="itemIndex === 0"
                    >
                      Quitar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-end gap-3 mt-6">
          <button
            class="px-4 py-2 rounded-md bg-slate-100 text-slate-600 text-sm font-semibold hover:bg-slate-200 disabled:opacity-60 disabled:cursor-not-allowed"
            @click="closeModal"
            :disabled="saving"
          >
            Cerrar
          </button>
          <button
            class="px-4 py-2 rounded-md bg-blue-500 text-white text-sm font-semibold hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed"
            @click="emitPromotions"
            :disabled="saving"
          >
            {{ saving ? "Guardando..." : "Guardar promociones" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onUnmounted, ref, watch } from "vue";
import axios from "@/api";
import { errorActions } from "@/alerts";
import { format, money } from "@/publicjs/money";
import {
  applyPromotionTypeDefaults,
  buildPromotionSummary,
  createPromotionDraft,
  createPromotionItem,
  createPromotionTarget,
  PROMOTION_TYPES,
} from "@/utils/promotions";

export default {
  name: "ProductPromotionModal",
  props: {
    activeModal: {
      type: Function,
      required: true,
    },
    promotions: {
      type: Array,
      required: false,
      default: () => [],
    },
    productName: {
      type: String,
      required: false,
      default: "",
    },
    productId: {
      type: Number,
      required: false,
      default: 0,
    },
    productBarCode: {
      type: String,
      required: false,
      default: "",
    },
    baseSalePrice: {
      type: Number,
      required: false,
      default: 0,
    },
    idStore: {
      type: Number,
      required: false,
      default: 0,
    },
    closeOnSave: {
      type: Boolean,
      required: false,
      default: true,
    },
    saving: {
      type: Boolean,
      required: false,
      default: false,
    },
    availableUnits: {
      type: Array,
      required: false,
      default: () => [],
    },
    allowProductSelection: {
      type: Boolean,
      required: false,
      default: false,
    },
    onProductSelected: {
      type: Function,
      required: false,
      default: null,
    },
  },
  emits: ["save"],
  setup(props, { emit }) {
    const localPromotions = ref([]);
    const searchTerms = ref({});
    const searchResults = ref({});
    const searchLoadingIndex = ref(null);
    const unitOptions = ref([]);
    const unitOptionsLoading = ref(false);
    const productSearchTerm = ref("");
    const productSearchResults = ref([]);
    const productSearchLoading = ref(false);
    const productSearchPerformed = ref(false);
    let productSearchDebounce = null;

    const createContext = () => ({
      productId: props.productId || null,
      productName: props.productName || "Producto principal",
    });

    const hasSelectedProduct = computed(() => Number(props.productId || 0) > 0);

    const modalTitle = computed(() => {
      if (props.allowProductSelection) {
        return "Nueva oferta";
      }

      return props.productId ? "Editar oferta" : "Aplicar oferta";
    });

    const modalDescription = computed(() => {
      if (props.allowProductSelection) {
        return "Busca el producto por nombre o codigo de barra y configura su oferta sin salir del submódulo.";
      }

      if (props.productId) {
        return "Ajusta las promociones existentes del producto usando el mismo motor comercial.";
      }

      return "Configura promociones persistentes sin mezclar la lógica comercial con el formulario principal.";
    });

    const showProductSearchResults = computed(() => {
      if (!props.allowProductSelection) {
        return false;
      }

      return productSearchLoading.value || (productSearchPerformed.value && productSearchTerm.value.trim().length > 0);
    });

    const normalizeUnitOption = (unit = {}) => {
      const productUnitMeasureId =
        unit.productUnitMeasureId ?? unit.targetUnitMeasureId ?? null;
      const quantityContained = Number(
        unit.quantityContained ?? unit.targetQuantityContained ?? 1,
      );
      const isPrincipal =
        Boolean(unit.isPrincipal) ||
        (!productUnitMeasureId && quantityContained <= 1);

      return {
        key:
          unit.key ||
          (productUnitMeasureId
            ? `unit-${productUnitMeasureId}`
            : `unit-${String(unit.unitMeasureName || unit.targetUnitMeasureName || "").trim().toLowerCase()}-${quantityContained}`),
        productUnitMeasureId,
        unitMeasureName:
          unit.unitMeasureName || unit.targetUnitMeasureName || "",
        quantityContained,
        currentPrice: Number(unit.currentPrice || unit.price || 0),
        label:
          unit.label ||
          unit.targetUnitLabel ||
          `${unit.unitMeasureName || unit.targetUnitMeasureName || "Presentacion"} x ${quantityContained}`,
        isPrincipal,
      };
    };

    const setUnitOptions = (units = []) => {
      const map = new Map();

      (Array.isArray(units) ? units : []).forEach((unit) => {
        const normalized = normalizeUnitOption(unit);
        map.set(normalized.key, normalized);
      });

      unitOptions.value = Array.from(map.values());
    };

    const applyTargetToPromotion = (promotion, unitOption) => {
      if (!unitOption) {
        Object.assign(promotion, createPromotionTarget());
        return;
      }

      const normalized = normalizeUnitOption(unitOption);

      Object.assign(
        promotion,
        createPromotionTarget({
          targetUnitMeasureId: normalized.productUnitMeasureId,
          targetUnitMeasureName: normalized.unitMeasureName,
          targetUnitLabel: normalized.label,
          targetQuantityContained: normalized.quantityContained,
        }),
      );
    };

    const findPromotionTargetOption = (promotion) => {
      const targetUnitMeasureId = promotion?.targetUnitMeasureId;
      const targetQuantityContained = Number(
        promotion?.targetQuantityContained || 1,
      );
      const targetUnitMeasureName = String(
        promotion?.targetUnitMeasureName || "",
      )
        .trim()
        .toUpperCase();

      const byId = unitOptions.value.find(
        (unitOption) =>
          Number(unitOption.productUnitMeasureId || 0) ===
          Number(targetUnitMeasureId || 0),
      );

      if (byId) {
        return byId;
      }

      const byNameAndQuantity = unitOptions.value.find((unitOption) => {
        return (
          String(unitOption.unitMeasureName || "").trim().toUpperCase() ===
            targetUnitMeasureName &&
          Number(unitOption.quantityContained || 1) === targetQuantityContained
        );
      });

      if (byNameAndQuantity) {
        return byNameAndQuantity;
      }

      return unitOptions.value[0] || null;
    };

    const ensurePromotionTarget = (promotion) => {
      applyTargetToPromotion(promotion, findPromotionTargetOption(promotion));
    };

    const ensureBaseItem = (promotion) => {
      if (!Array.isArray(promotion.items) || promotion.items.length <= 0) {
        promotion.items = [
          createPromotionItem({
            productId: props.productId || null,
            name: props.productName || "Producto principal",
            role: "REQUERIDO",
            quantity: 1,
          }),
        ];
      } else {
        promotion.items[0].name = props.productName || promotion.items[0].name;
        if (props.productId) {
          promotion.items[0].productId = props.productId;
        }
      }
    };

    const syncLocalPromotions = () => {
      localPromotions.value = (props.promotions || []).length
        ? JSON.parse(JSON.stringify(props.promotions)).map((promotion) =>
            applyPromotionTypeDefaults(promotion)
          )
        : [createPromotionDraft(createContext())];

      localPromotions.value.forEach((promotion) => {
        ensureBaseItem(promotion);
        ensurePromotionTarget(promotion);
      });
    };

    const syncUnitOptions = async () => {
      unitOptionsLoading.value = true;

      if (Array.isArray(props.availableUnits) && props.availableUnits.length > 0) {
        setUnitOptions(props.availableUnits);
        unitOptionsLoading.value = false;
        localPromotions.value.forEach((promotion) => ensurePromotionTarget(promotion));
        return;
      }

      if (!props.productId) {
        setUnitOptions([]);
        unitOptionsLoading.value = false;
        localPromotions.value.forEach((promotion) => ensurePromotionTarget(promotion));
        return;
      }

      try {
        const response = await axios.get("productos-unidad-de-medida", {
          params: {
            idproduct: props.productId,
          },
        });

        const payload = response.data?.data || response.data || [];
        setUnitOptions(
          (Array.isArray(payload) ? payload : []).map((unit) => ({
            key: `unit-${unit.id}`,
            productUnitMeasureId: unit.id,
            unitMeasureName: unit.um || "",
            quantityContained: Number(unit.cantidad || 1),
            label: `${unit.um} x ${Number(unit.cantidad || 1)}`,
            currentPrice: Number(unit.precio || 0),
          })),
        );
      } catch (error) {
        setUnitOptions([]);
        errorActions(
          error?.response?.data?.detail ||
            "No pudimos cargar las presentaciones del producto.",
        );
      } finally {
        unitOptionsLoading.value = false;
        localPromotions.value.forEach((promotion) => ensurePromotionTarget(promotion));
      }
    };

    watch(
      () => [props.promotions, props.productName, props.productId],
      () => syncLocalPromotions(),
      { immediate: true, deep: true }
    );

    watch(
      () => [props.productId, props.availableUnits],
      () => syncUnitOptions(),
      { immediate: true, deep: true },
    );

    onUnmounted(() => {
      if (productSearchDebounce) {
        clearTimeout(productSearchDebounce);
      }
    });

    const promotionSummary = computed(() =>
      buildPromotionSummary(localPromotions.value)
    );

    const closeModal = () => {
      props.activeModal(false);
    };

    const searchSelectableProducts = async () => {
      const term = productSearchTerm.value.trim();
      if (!term || !props.idStore) {
        productSearchResults.value = [];
        productSearchPerformed.value = false;
        return;
      }

      productSearchLoading.value = true;
      productSearchPerformed.value = true;
      try {
        const response = await axios.get("productos", {
          params: {
            idstore: props.idStore,
            search: term,
            per_page: 10,
          },
        });

        const payload = response.data?.data || response.data || [];
        productSearchResults.value = Array.isArray(payload) ? payload : [];
      } catch (error) {
        errorActions(error);
        productSearchResults.value = [];
      } finally {
        productSearchLoading.value = false;
      }
    };

    const handleProductSearchInput = () => {
      const term = productSearchTerm.value.trim();

      if (productSearchDebounce) {
        clearTimeout(productSearchDebounce);
      }

      if (!term) {
        productSearchResults.value = [];
        productSearchPerformed.value = false;
        return;
      }

      productSearchDebounce = setTimeout(() => {
        searchSelectableProducts();
      }, 250);
    };

    const selectProductForOffer = async (product) => {
      if (typeof props.onProductSelected !== "function") {
        return;
      }

      productSearchLoading.value = true;
      try {
        const payload = await props.onProductSelected(product);
        const nextPromotions = Array.isArray(payload?.promotions) && payload.promotions.length > 0
          ? payload.promotions
          : [
              createPromotionDraft({
                productId: Number(payload?.productId || product.id || 0),
                productName: payload?.productName || product.nombre_producto || product.name || "Producto principal",
              }),
            ];

        localPromotions.value = JSON.parse(JSON.stringify(nextPromotions)).map((promotion) =>
          applyPromotionTypeDefaults(promotion)
        );
        localPromotions.value.forEach((promotion) => {
          ensureBaseItem(promotion);
          ensurePromotionTarget(promotion);
        });
        productSearchResults.value = [];
        productSearchPerformed.value = false;
        productSearchTerm.value = payload?.productName || product.nombre_producto || product.name || "";
      } catch (error) {
        errorActions(error?.response?.data?.detail || "No pudimos cargar las promociones del producto seleccionado.");
      } finally {
        productSearchLoading.value = false;
      }
    };

    const addPromotion = () => {
      const promotion = createPromotionDraft(createContext());
      ensurePromotionTarget(promotion);
      localPromotions.value.push(promotion);
    };

    const removePromotion = (index) => {
      localPromotions.value.splice(index, 1);
      if (localPromotions.value.length <= 0) {
        localPromotions.value.push(createPromotionDraft(createContext()));
      }
    };

    const handleTypeChange = (promotion) => {
      Object.assign(
        promotion,
        applyPromotionTypeDefaults(promotion, { overwrite: true })
      );
      ensureBaseItem(promotion);
      ensurePromotionTarget(promotion);
      if (promotion.type !== PROMOTION_TYPES.BUNDLE) {
        promotion.items = [promotion.items[0]];
      }
    };

    const getPromotionTargetKey = (promotion) =>
      findPromotionTargetOption(promotion)?.key || "";

    const changePromotionTarget = (promotion, unitKey) => {
      const unitOption =
        unitOptions.value.find((currentUnit) => currentUnit.key === unitKey) ||
        unitOptions.value[0] ||
        null;

      applyTargetToPromotion(promotion, unitOption);
    };

    const getPromotionTypeLabel = (type) => {
      if (type === PROMOTION_TYPES.QUANTITY_BONUS) {
        return "Cantidad bonificada";
      }

      if (type === PROMOTION_TYPES.BUNDLE) {
        return "Lote, combo o paquete";
      }

      return "Precio especial";
    };

    const getCurrentPriceValue = (promotion) => {
      const selectedUnit = findPromotionTargetOption(promotion);
      if (selectedUnit && Number(selectedUnit.currentPrice || 0) > 0) {
        return Number(selectedUnit.currentPrice || 0);
      }

      return Number(props.baseSalePrice || 0);
    };

    const getCurrentPriceLabel = (promotion) => {
      const currentPrice = getCurrentPriceValue(promotion);
      if (currentPrice <= 0) {
        return "No disponible";
      }

      return `${money()} ${format(currentPrice)}`;
    };

    const searchProducts = async (index) => {
      const term = (searchTerms.value[index] || "").trim();
      if (!term || !props.idStore) {
        searchResults.value[index] = [];
        return;
      }

      searchLoadingIndex.value = index;
      try {
        const response = await axios.get("productos", {
          params: {
            idstore: props.idStore,
            search: term,
            per_page: 10,
          },
        });

        const payload = response.data?.data || response.data || [];
        searchResults.value[index] = Array.isArray(payload) ? payload : [];
      } catch (error) {
        errorActions(error);
        searchResults.value[index] = [];
      } finally {
        searchLoadingIndex.value = null;
      }
    };

    const addCompanionProduct = (promotionIndex, result) => {
      const promotion = localPromotions.value[promotionIndex];
      ensureBaseItem(promotion);

      const exists = promotion.items.some(
        (item) => Number(item.productId) === Number(result.id)
      );
      if (exists) {
        return;
      }

      promotion.items.push(
        createPromotionItem({
          productId: result.id,
          name: result.nombre_producto,
          role: "REQUERIDO",
          quantity: 1,
        })
      );
    };

    const removePromotionItem = (promotionIndex, itemIndex) => {
      if (itemIndex === 0) {
        return;
      }

      localPromotions.value[promotionIndex].items.splice(itemIndex, 1);
    };

    const emitPromotions = () => {
      if (props.allowProductSelection && !hasSelectedProduct.value) {
        errorActions("Primero debes seleccionar el producto al que vas a registrar la oferta.");
        return;
      }

      const invalidDates = localPromotions.value.find((promotion) => {
        if (!promotion.startAt || !promotion.endAt) {
          return false;
        }

        return new Date(promotion.endAt).getTime() <= new Date(promotion.startAt).getTime();
      });

      if (invalidDates) {
        errorActions("La fecha de fin debe ser mayor que la fecha de inicio.");
        return;
      }

      const invalidSpecialPrice = localPromotions.value.find(
        (promotion) =>
          promotion.type === PROMOTION_TYPES.SPECIAL_PRICE &&
          Number(promotion.specialPrice || 0) <= 0
      );

      if (invalidSpecialPrice) {
        errorActions("Toda promocion de precio especial debe tener un precio valido.");
        return;
      }

      const invalidSpecialPriceEndDate = localPromotions.value.find(
        (promotion) =>
          promotion.type === PROMOTION_TYPES.SPECIAL_PRICE &&
          (!promotion.endAt || String(promotion.endAt).trim() === "")
      );

      if (invalidSpecialPriceEndDate) {
        errorActions("La oferta de precio especial requiere una fecha de fin para poder devolver el precio original automáticamente.");
        return;
      }

      const invalidSpecialPriceAgainstCurrent = localPromotions.value.find(
        (promotion) => {
          if (promotion.type !== PROMOTION_TYPES.SPECIAL_PRICE) {
            return false;
          }

          const currentPrice = getCurrentPriceValue(promotion);
          if (currentPrice <= 0) {
            return false;
          }

          return Number(promotion.specialPrice || 0) >= currentPrice;
        }
      );

      if (invalidSpecialPriceAgainstCurrent) {
        errorActions("El precio de oferta debe ser menor que el precio actual del producto en la unidad seleccionada.");
        return;
      }

      const invalidBonus = localPromotions.value.find(
        (promotion) =>
          promotion.type === PROMOTION_TYPES.QUANTITY_BONUS &&
          (Number(promotion.requiredQuantity || 0) <= 0 ||
            Number(promotion.bonusQuantity || 0) <= 0)
      );

      if (invalidBonus) {
        errorActions("La promocion por cantidad bonificada requiere cantidades validas.");
        return;
      }

      const invalidBundle = localPromotions.value.find(
        (promotion) =>
          promotion.type === PROMOTION_TYPES.BUNDLE &&
          (Number(promotion.bundlePrice || 0) <= 0 || promotion.items.length <= 0)
      );

      if (invalidBundle) {
        errorActions("El paquete debe tener un precio de paquete y al menos un producto configurado.");
        return;
      }

      const invalidUnitTarget = localPromotions.value.find(
        (promotion) => !promotion.targetUnitLabel || Number(promotion.targetQuantityContained || 0) <= 0
      );

      if (invalidUnitTarget) {
        errorActions("Cada oferta debe estar asociada a una unidad de medida existente del producto.");
        return;
      }

      emit("save", JSON.parse(JSON.stringify(localPromotions.value)));
      if (props.closeOnSave) {
        closeModal();
      }
    };

    return {
      PROMOTION_TYPES,
      addCompanionProduct,
      addPromotion,
      changePromotionTarget,
      closeModal,
      emitPromotions,
      handleProductSearchInput,
      hasSelectedProduct,
      getCurrentPriceLabel,
      getPromotionTargetKey,
      getPromotionTypeLabel,
      handleTypeChange,
      localPromotions,
      modalDescription,
      modalTitle,
      promotionSummary,
      productSearchLoading,
      productSearchPerformed,
      productSearchResults,
      productSearchTerm,
      removePromotion,
      removePromotionItem,
      searchLoadingIndex,
      searchSelectableProducts,
      searchProducts,
      searchResults,
      searchTerms,
      showProductSearchResults,
      selectProductForOffer,
      unitOptions,
      unitOptionsLoading,
    };
  },
};
</script>